// Helper functions for Schedule handling
import type { ScheduleType } from '@/types/appTypes';

import { toZonedTime } from 'date-fns-tz';

function makeZurichMidnightDate(dateStr: string): Date {
    const [year, month, day] = dateStr.split('-').map(Number);
    // Create UTC midnight for the given date
    const utcMidnight = new Date(Date.UTC(year, month - 1, day));
    // Convert to Zurich midnight (same day, 00:00 local time)
    return toZonedTime(utcMidnight, 'Europe/Zurich');
}


/**
 * @param checkDate     - The date to check against the schedules, in 'YYYY-MM-DD' format.
 * @param baseScheds    - Array of base schedules.
 * @param overScheds    - Array of override schedules.
 * @returns
 */
export function getActiveSchedule(checkDateStr: string, baseScheds: ScheduleType[], overScheds: ScheduleType[]): ScheduleType | null {


    if (overScheds) {
        const foundOVERRIDE = getActiveOVERRIDE(checkDateStr, overScheds);
        if (foundOVERRIDE) {
            // If a Override schedule is found, return it immediately
            return foundOVERRIDE;
        }
    }

    if (baseScheds) {
        // const checkDate = makeZurichMidnightDate(checkDateStr);
        const foundBASE = getActiveBASE(checkDateStr, baseScheds);
        if (foundBASE) {
            // If a BASE schedule is found, return it immediately
            return foundBASE;
        }
    }

    // Nothing found. Can happen if there's a Gap in Base.
    return null;
}

/**
 * Find the matching OVERRIDE scheudle for a given date.
 */
export function getActiveOVERRIDE(checkDate: string, overScheds: ScheduleType[]): ScheduleType | null {
    if (!overScheds || overScheds.length === 0) {
        return null;
    }

    for (let i = overScheds.length - 1; i >= 0; i--) {
        const overSched = overScheds[i];
        // Check if the checkDate is within the override schedule's date range
        // Ensure start_date and end_date are not null before comparing
        if (overSched.start_date && overSched.end_date && overSched.deleted_at === null) {
            // const startDate = makeZurichMidnightDate(overSched.start_date);
            // const endDate = makeZurichMidnightDate(overSched.end_date);
            // Check if checkDate is within the override schedule's date range
            if (checkDate >= overSched.start_date && checkDate <= overSched.end_date) {
                return overSched;
            }
        }
    }
    // Nothing found. Can happen if there's a Gap in Base.
    return null;
}

/**
 * Find the matching BASE scheudle for a given date.
 */
export function getActiveBASE(checkDateStr: string, baseScheds: ScheduleType[]): ScheduleType | null {
    if (!baseScheds || baseScheds.length === 0) {
        return null;
    }
    const checkDate = makeZurichMidnightDate(checkDateStr);

    // No Override found. Search Base schedules.
    // Base dates are just 'MM-DD'
    // Need to handle the whole 'Wrap' issue here as well.
    for (let i = baseScheds.length - 1; i >= 0; i--) {
        const baseSched = baseScheds[i];

        // Ensure start_date and end_date are defined and schedule is not deleted
        if (baseSched.start_date && baseSched.end_date && baseSched.deleted_at === null) {
            // Parse start and end month/day parts
            const startBits = baseSched.start_date.split('-'); // [MM, DD]
            const endBits = baseSched.end_date.split('-'); // [MM, DD]

            const checkYear = checkDate.getFullYear();

            const startMonth = parseInt(startBits[0], 10);
            const startDay = parseInt(startBits[1], 10);

            const endMonth = parseInt(endBits[0], 10);
            const endDay = parseInt(endBits[1], 10);

            let startYear = checkYear;
            let endYear = checkYear;

            // Detect if schedule wraps across the year boundary
            const isWrap = startMonth > endMonth || (startMonth === endMonth && startDay > endDay);

            if (isWrap) {
                // If checkDate is before or on the endMonth-endDay, then startYear is previous year
                if (checkDate.getMonth() + 1 < endMonth || (checkDate.getMonth() + 1 === endMonth && checkDate.getDate() <= endDay)) {
                    startYear = checkYear - 1;
                } else {
                    // Otherwise, endYear moves to next year
                    endYear = checkYear + 1;
                }
            }

            const convertedStartDate = makeZurichMidnightDate(`${startYear}-${startBits[0]}-${startBits[1]}`);
            const convertedEndDate = makeZurichMidnightDate(`${endYear}-${endBits[0]}-${endBits[1]}`);

            // Check if the checkDate falls within the schedule range (inclusive)
            if (checkDate >= convertedStartDate && checkDate <= convertedEndDate) {
                return baseSched;
            }
        }
    }

    // Nothing found. Can happen if there's a Gap in Base.
    return null;
}

/**
 * Find the matching schedule for a Schedule ID.
 * @param scheduleId - The ID of the schedule to find.
 * @param baseScheds - Array of base schedules.
 * @param overScheds - Array of override schedules.
 * @returns The matching schedule or null if not found.
 */
export function findScheduleById(scheduleId: number, baseScheds: ScheduleType[], overScheds: ScheduleType[]): ScheduleType | null {
    // Check in override schedules first
    const overSched = overScheds.find((sched) => sched.id === scheduleId);
    if (overSched) {
        overSched.type = 'override'; // Set type for override
        return overSched;
    }

    // Check in base schedules
    const baseSched = baseScheds.find((sched) => sched.id === scheduleId);
    if (baseSched) {
        baseSched.type = 'base'; // Set type for base
        return baseSched;
    }

    // Not found
    return null;
}
